/** 금액·진행률 계산.
 *
 * **계산이 한 곳에 있어야 하는 이유가 이 제품에는 기록으로 남아 있다.** 목업이 합계를
 * `₩192` 로 썼다가 `7장 × ₩27 = ₩189` 로 정정됐다. 화면마다 곱셈을 다시 쓰면 같은 어긋남이
 * 또 생기고, 그게 돈에 관한 숫자면 셀러가 제품을 믿지 않는다.
 *
 * ## 이 파일에는 성격이 다른 두 금액이 있다. 섞지 마라
 *
 * | | 누가 내는가 | 상수 | 곱셈 함수 | 어디에 쓰나 |
 * |---|---|---|---|---|
 * | **원가** | 우리가 provider 에 낸다 | `CUTOUT_UNIT_COST_KRW` | `cutoutProviderCost` | 마진 계산 · 원가 표 · `BillingItem.providerCostAmount` |
 * | **청구가** | 셀러가 우리에게 낸다 | `CUTOUT_UNIT_PRICE_KRW` | `cutoutPrice` · `remakePrice` | 셀러가 보는 **모든** 금액 |
 *
 * ⛔ **셀러가 보는 자리에 원가를 적지 않는다. 청구가로 마진을 계산하지 않는다.**
 * 둘이 섞이는 사고는 이름에서 시작한다. 그래서 이름에 `cost`(원가)와 `price`(청구가)를
 * 갈라 배정했고, 한쪽 이름으로 다른 쪽 값을 돌려주는 함수를 새로 만들지 않는다.
 *
 * ⚠️ **위 표의 `BillingItem.providerCostAmount` 는 컬럼이 있을 뿐 아직 한 행도 없다.**
 * `apps/api` 전수에 `BillingItem` 으로 가는 `INSERT` 가 0 건이다(2026-09-13 실측).
 * **그러므로 지금 이 제품에서 실측 원가가 적히는 자리는 없다.** 원가를 역산할 재료는
 * `JobImage.providerCallCount`(몇 번 불렀나) 하나뿐이고, 그 값에 이 파일의 상수를 곱하는
 * 것은 실측이 아니라 계획값이다. 결제가 붙을 때 그 칸을 채우는 것이 원가 쪽 다음 일이다.
 */

import type { DetailJobView } from "./domain";
import { doneImages, failedImages, pendingImages } from "./job-rules";

/** 배경 제거 **원가** — 장당 약 ₩27 ($0.02). 이 제품의 유일한 변동비다(PRD 4.2 / S4).
 *
 * **우리가 Photoroom 에 내는 돈이다. 셀러 청구가가 아니다**(그쪽은
 * `CUTOUT_UNIT_PRICE_KRW`). 청구가가 확정된 뒤에도 이 상수는 없어지지 않는다. 마진과
 * 원가 표가 이 값을 쓰고, 장별 실측 원가는 `BillingItem.providerCostAmount` 에 행마다
 * 남는다(벤더를 바꾸면 값이 달라져 상수로 못 박을 수 없다).
 *
 * ⛔ **이 값을 화면에 적지 마라.** 원가를 셀러에게 보여주면 마진이 그대로 공개된다.
 * 청구가가 미정이던 동안 목업이 화면에 `₩27` 을 쓰고 있었는데, 그건 가격이 없어서였지
 * 원가를 보여주기로 정해서가 아니었다(2026-09-13 청구가 확정으로 닫혔다).
 *
 * ⚠️ **지금 이 상수를 읽는 코드는 바로 아래 `cutoutProviderCost` 하나뿐이고, 그 함수도
 * 호출부가 0 이다**(2026-09-13 저장소 전수 실측. 앱이 `₩27` 을 직접 읽던 두 자리는
 * 청구가로 갈아탔다). **그 0 이 이 상수를 지울 근거가 아니다.** 원가를 쓸 코드가 아직
 * 없는 것은 **결제가 안 붙어서**이지 원가가 없어서가 아니다. 여기서 지우면 결제를 붙이는
 * 사람이 원가 상수를 찾다 못 찾고 `CUTOUT_UNIT_PRICE_KRW` 를 원가 자리에 쓴다. 그 사고를
 * 미리 막는 것이 이 상수가 지금 하는 일 전부다. */
export const CUTOUT_UNIT_COST_KRW = 27;

/** 배경 제거 **셀러 청구가**. 장당 ₩500. **2026-09-13 사용자 확정.**
 *
 * `cost.ts` 가 "가격이 정해지면 상수를 따로 두고 화면이 그쪽을 보게 한다"고 미리 적어 둔
 * 자리를 채운 것이다. **원가(₩27)는 그대로 둔다.** 바뀐 것은 셀러가 내는 값 하나뿐이다.
 *
 * ⚠️ **이 상수가 답하지 않는 것 세 가지.** 사용자가 말한 것은 "장당 500원" 하나뿐이라
 * 나머지를 여기서 지어내지 않는다(`docs/DATA_DICTIONARY.md` 14장).
 *  1. **부가세가 포함인가.** ⭐ **2026-09-14 `13.1` O-44 로 닫혔다. 부가세 포함이다.**
 *     그래서 공급가는 ₩455 쯤(500 / 1.1)이고, 마진은 `500 - 27` 이 아니다. `500 - 27` 은
 *     "500 이 부가세 별도"를 가정한 식이었고 **그 가정은 이제 틀렸다.** 이 파일에는 지금도
 *     **마진 함수가 없다**(값·계산 0줄). 마진을 계산하는 코드를 새로 쓸 때는 공급가에서
 *     원가를 빼야 하고, 원 단위 단수 처리는 PRD O-44 의 단수 처리 항목을 따른다.
 *  2. **작업 1건 단가(PRD 4.4 의 "작업 1건 ₩1,900" 제안)와 어떻게 겹치는가.** 장당과
 *     건당이 둘 다 살아 있는지, 건당이 폐기됐는지 안 정해졌다. `prd-manager` 소관이다.
 *  3. **다시 만들기가 같은 단가인가.** `remakePrice` 주석을 보라. */
export const CUTOUT_UNIT_PRICE_KRW = 500;

/** 셀러에게 청구하는 금액. **셀러가 보는 모든 금액의 정본이다.** 결제 확인(`A-26`)의
 *  합계, 결제 이력의 줄 금액, 정산 명세의 돌아간 금액이 전부 이 함수다.
 *
 * `imageCount` 는 **청구 장수**다. 올린 장수가 아니다. 둘이 갈리는 경로가 실재한다
 * (`providerCallCount = 0` 으로 끝난 장은 청구도 환불도 없다.
 * `billing-history.ts` 의 `uploadedQuantity` 주석). */
export function cutoutPrice(imageCount: number): number {
  return imageCount * CUTOUT_UNIT_PRICE_KRW;
}

/** 다시 만들기(`A-25`) 한 장에 셀러가 더 내는 금액.
 *
 * **첫 청구와 같은 단가를 쓴다.** 다시 만들기가 `BillingItem.kind = 'CUTOUT_RETRY'` 로
 * 갈라져 있어 단가를 달리 매길 자리는 이미 있지만, **사용자가 정한 것은 "장당 500원"
 * 하나이고 다시 만들기를 따로 말하지 않았다.** 그래서 갈래를 지어내지 않고 같은 값을
 * 본다. 달라지면 이 함수만 바뀐다(그래서 `cutoutPrice(1)` 로 뭉개지 않고 함수를 남겼다).
 *
 * `DATA_DICTIONARY` 8장 "청구 단가가 되살리기와 다시 만들기에서 달라지면" 이 그 자리다. */
export function remakePrice(imageCount = 1): number {
  return imageCount * CUTOUT_UNIT_PRICE_KRW;
}

/** 그 장수에 **우리가** 드는 원가. 마진·원가 표용이고 **셀러 화면에 쓰지 않는다.**
 *
 * 청구가 함수가 생겼다고 원가 쪽 곱셈이 없어지면, 다음 사람이 원가를 계산할 때
 * `cutoutPrice` 를 재사용한다. 그 사고를 막으려고 호출부가 아직 없어도 이름을 세워 둔다.
 *
 * ⚠️ **실측 원가는 이 함수가 아니라 `BillingItem.providerCostAmount` 다.** 이 상수는
 * 계획값이고, 벤더·환율·요금제가 바뀌면 행에 박힌 값과 갈린다. 정산·마진 집계는 행을 쓴다. */
export function cutoutProviderCost(imageCount: number): number {
  return imageCount * CUTOUT_UNIT_COST_KRW;
}

/* `cutoutCost` · `retryCost` 를 여기서 지웠다 (2026-09-13, 저장소 전수 실측 호출부 0건).
 *
 * **이름이 `cost` 인데 청구가를 돌려주던 함수 둘**이다. 청구가 확정 전에는 원가를 곱했고,
 * **앱·웹 16파일 30자리 호출부가 전부 셀러가 보는 금액이었다.** 즉 원가를 곱하고 싶었던
 * 호출부는 하나도 없었다. 그래서 값을 청구가로 돌려놓고 이름만 남겨 뒀는데, 그 뒤
 * `apps/api/src/routes/jobs.ts` 까지 `cutoutPrice`·`remakePrice` 로 옮겨 와 **부르는 곳이
 * 없어졌다.**
 *
 * ⛔ **되살리지 마라.** 이름이 원가인데 청구가를 주는 함수가 살아 있으면, 결제를 붙이는
 * 사람이 그것을 원가 자리에 쓴다. 원가는 `cutoutProviderCost` 이고 청구가는
 * `cutoutPrice`·`remakePrice` 다. `retry` 라는 낱말도 이제 옛말이다 — 셀러가 보는 말은
 * `다시 만들기` 로 닫혔다(PRD `7.5.9` D-62). */

export interface JobProgress {
  /** 전체 장수 */
  total: number;
  /** 끝난 장수(성공) */
  done: number;
  /** 실패한 장수 */
  failed: number;
  /** 아직 안 온 장수 */
  pending: number;
  /** 0~1. 실패도 "결판난 장"이라 분자에 넣는다 — 실패분을 빼면 막대가 영원히 안 찬다. */
  ratio: number;
}

export function jobProgress(job: DetailJobView): JobProgress {
  const total = job.images.length;
  const done = doneImages(job).length;
  const failed = failedImages(job).length;
  const pending = pendingImages(job).length;
  return {
    total,
    done,
    failed,
    pending,
    ratio: total === 0 ? 0 : (done + failed) / total,
  };
}
